<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import {
  NCard,
  NButton,
  NSpace,
  NTag,
  NList,
  NListItem,
  NAvatar,
  NBadge,
  NIcon,
  NEmpty,
  NSpin,
  NPageHeader,
  NProgress,
  NAlert,
  NDivider,
  NUpload,
  NUploadDragger,
  NModal,
  NPopover,
  NCollapse,
  NCollapseItem,
  NInput,
  NForm,
  NFormItem
} from "naive-ui";
import { 
  User as ProfileIcon, 
  Edit as EditIcon,
  AlertCircle as InfoIcon,
  Scale,
  Briefcase,
  History,
  CheckSquare,
  AlertTriangle,
  FileText,
  User,
  Camera,
  Upload as UploadIcon,
  X as CloseIcon,
  Shield,
  Heart,
  Calendar,
  MessageCircle as Communication,
  Users,
  Clipboard,
  Award,
  BookOpen,
  Coffee,
  Home,
  CalendarCheck,
  Activity,
  Utensils,
  Accessibility as AccessibilityIcon,
  Lock,
  Key,
  Building,
  DollarSign,
  MapPin,
  Phone,
  Mail,
  CreditCard,
  Gavel,
  Clock,
  Star,
  UserCheck,
  TrendingUp,
  Database,
  FileCheck,
  Building2,
  Banknote
} from "lucide-vue-next";

// API utilities
import { 
  fetchMyClientProfile, 
  uploadClientProfileImage, 
  getClientProfileImage 
} from "@/services/client";
import { authToken, isAuthenticated } from "@/services/auth";

// Encryption utilities
import { decryptClientData } from "@/services/encryption";

const router = useRouter();

// Page state
const loading = ref(true);
const error = ref(null);
const clientProfile = ref(null);
const encryptedClientData = ref(null);
const profileImageUrl = ref(null);
const isUploadingImage = ref(false);
const imageUploadError = ref(null);
const showImageModal = ref(false);

// Password prompt state
const showPasswordModal = ref(false);
const decryptionPassword = ref("");
const passwordError = ref(null);
const isDecrypting = ref(false);

// Computed property to normalize property names
const normalizedProfile = computed(() => {
  if (!clientProfile.value) return null;
  
  const profile = clientProfile.value;
  
  return {
    // Keep original properties
    ...profile,
    
    // Add normalized properties with snake_case names for template compatibility
    full_name: profile.fullName || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'Client',
    emergency_contact_name: profile.emergencyContactName,
    emergency_contact_phone: profile.emergencyContactPhone,
    emergency_contact_relationship: profile.emergencyContactRelationship,
    legal_matters: profile.legalMatters,
    previous_legal_issues: profile.previousLegalIssues,
    current_legal_cases: profile.currentLegalCases,
    client_type: profile.clientType,
    income_level: profile.incomeLevel,
    employment_status: profile.employmentStatus,
    business_type: profile.businessType,
    business_industry: profile.businessIndustry,
    business_size: profile.businessSize,
    business_revenue: profile.businessRevenue,
    legal_budget: profile.legalBudget,
    urgency_level: profile.urgencyLevel,
    referral_source: profile.referralSource,
    previous_attorneys: profile.previousAttorneys,
    legal_documents: profile.legalDocuments,
    court_cases: profile.courtCases,
    personal_info: profile.personalInfo,
    financial_info: profile.financialInfo,
    accessibility_needs: profile.accessibilityNeeds,
    special_requirements: profile.specialRequirements,
    conflict_of_interest: profile.conflictOfInterest,
    background_check: profile.backgroundCheck,
    preferred_communication_method: profile.preferredCommunicationMethod
  };
});

onMounted(async () => {
  if (!isAuthenticated.value) {
    router.push('/login');
    return;
  }

  await loadClientData();
});

const loadClientData = async () => {
  try {
    loading.value = true;
    
    // Fetch encrypted client profile
    const profileData = await fetchMyClientProfile(authToken.value);
    
    if (profileData) {
      // Store the encrypted data
      encryptedClientData.value = profileData;
      
      // Show password prompt to decrypt
      showPasswordModal.value = true;
      
      // Fetch profile image (this is not encrypted)
      try {
        const imageResult = await getClientProfileImage(authToken.value);
        if (imageResult.success && imageResult.image_url) {
          profileImageUrl.value = imageResult.image_url;
        }
      } catch (imageError) {
        console.error('Error loading profile image:', imageError);
      }
    }
  } catch (err) {
    error.value = "Failed to load client profile";
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const decryptData = async () => {
  if (!decryptionPassword.value.trim()) {
    passwordError.value = "Please enter your client password";
    return;
  }

  try {
    isDecrypting.value = true;
    passwordError.value = null;

    // Decrypt the client data
    const decryptedData = await decryptClientData(
      encryptedClientData.value.encrypted_client_data,
      encryptedClientData.value.encryption_metadata,
      decryptionPassword.value
    );

    // Store decrypted data
    clientProfile.value = decryptedData;

    console.log("clientProfile is ", clientProfile.value);
    
    // Close password modal
    showPasswordModal.value = false;
    decryptionPassword.value = "";

  } catch (err) {
    console.error('Decryption failed:', err);
    passwordError.value = "Invalid password. Please try again.";
  } finally {
    isDecrypting.value = false;
  }
};

const cancelDecryption = () => {
  showPasswordModal.value = false;
  decryptionPassword.value = "";
  passwordError.value = null;
  router.push('/client-profile');
};

// Format date helper with more details
const formatDate = (dateString) => {
  if (!dateString) return "Not provided";
  
  const date = new Date(dateString);
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
};

// Format phone number
const formatPhoneNumber = (phone) => {
  if (!phone) return null;
  
  // Basic formatting for a 10-digit number
  if (phone.length === 10) {
    return `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}`;
  }
  
  return phone;
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount) return "Not provided";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

// Parse JSON data for display
const parseJsonData = (jsonData) => {
  if (!jsonData || jsonData.length === 0) return [];
  return Array.isArray(jsonData) ? jsonData : [jsonData];
};

// Render personal info in a readable format
const formatPersonalInfo = (info) => {
  if (!info || Object.keys(info).length === 0) return null;
  
  try {
    const sections = [];
    for (const [key, value] of Object.entries(info)) {
      // Format each key from camelCase or snake_case to readable text
      const formattedKey = key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase());
      
      // Format value based on type
      let formattedValue = value;
      if (typeof value === 'boolean') {
        formattedValue = value ? 'Yes' : 'No';
      } else if (Array.isArray(value)) {
        formattedValue = value.join(', ');
      }
      
      sections.push({ key: formattedKey, value: formattedValue });
    }
    return sections;
  } catch (e) {
    console.error('Error formatting personal info:', e);
    return null;
  }
};

// Render financial info in a readable format
const formatFinancialInfo = (info) => {
  if (!info || Object.keys(info).length === 0) return null;
  
  try {
    const sections = [];
    for (const [key, value] of Object.entries(info)) {
      if (key === 'assets' || key === 'liabilities') return; // Handle these separately
      
      const formattedKey = key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase());
      
      let formattedValue = value;
      if (typeof value === 'boolean') {
        formattedValue = value ? 'Yes' : 'No';
      } else if (key.includes('income') || key.includes('amount')) {
        formattedValue = formatCurrency(value);
      }
      
      sections.push({ key: formattedKey, value: formattedValue });
    }
    return sections;
  } catch (e) {
    console.error('Error formatting financial info:', e);
    return null;
  }
};

// Navigate to edit profile
const editProfile = () => {
  router.push('/client-profile/edit');
};

// Handle image upload
const handleImageUpload = async (options) => {
  const { file } = options;
  
  try {
    isUploadingImage.value = true;
    imageUploadError.value = null;
    
    // Upload the image
    const result = await uploadClientProfileImage(authToken.value, file.file);
    
    // Update profile image URL
    if (result && result.image_url) {
      profileImageUrl.value = result.image_url;
      options.onFinish();
    }
    
    // Close modal after successful upload
    showImageModal.value = false;
    
  } catch (err) {
    imageUploadError.value = err.message || "Failed to upload image";
    options.onError();
  } finally {
    isUploadingImage.value = false;
  }
};

// Open image upload modal
const openImageModal = () => {
  showImageModal.value = true;
  imageUploadError.value = null;
};

// Close image upload modal
const closeImageModal = () => {
  showImageModal.value = false;
  imageUploadError.value = null;
};

// View larger profile image
const viewLargerImage = () => {
  if (profileImageUrl.value) {
    window.open(profileImageUrl.value, '_blank');
  }
};
</script>

<template>
  <div class="client-dashboard-container">
    
    <div v-if="loading" class="loading-container">
      <NSpin size="large" />
      <p>Loading your profile...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <NAlert type="error" :title="error" />
    </div>

    <div v-else>
      <!-- Show encrypted state if no decrypted data -->
      <div v-if="!clientProfile && encryptedClientData" class="encrypted-state">
        <NCard class="encryption-info-card">
          <div class="encryption-info">
            <NIcon size="48" class="lock-icon">
              <Lock />
            </NIcon>
            <h3>Client Profile Encrypted</h3>
            <p>Your legal data is encrypted and secure. Enter your client password to view your profile.</p>
            <NButton type="primary" @click="showPasswordModal = true" round>
              <template #icon>
                <NIcon>
                  <Key />
                </NIcon>
              </template>
              Decrypt Profile
            </NButton>
          </div>
        </NCard>
      </div>

      <!-- Show profile if data is decrypted -->
      <template v-if="normalizedProfile">
        <!-- Profile Header Card -->
        <NCard class="profile-header">
          <div class="profile-header-content">
            <div class="profile-avatar-container">
              <NPopover trigger="hover" placement="bottom">
                <template #trigger>
                  <NAvatar 
                    round 
                    size="large" 
                    :src="profileImageUrl" 
                    class="profile-avatar" 
                    @click="viewLargerImage"
                  >
                    <template v-if="!profileImageUrl">
                      <NIcon size="30">
                        <User />
                      </NIcon>
                    </template>
                  </NAvatar>
                </template>
                <div class="avatar-popover">
                  <p>{{ profileImageUrl ? 'Click to view full image' : 'No profile image' }}</p>
                  <NButton 
                    size="small" 
                    @click="openImageModal" 
                    type="primary" 
                    round
                  >
                    <template #icon>
                      <NIcon>
                        <Camera />
                      </NIcon>
                    </template>
                    {{ profileImageUrl ? 'Change Image' : 'Add Image' }}
                  </NButton>
                </div>
              </NPopover>
              
              <NButton 
                class="camera-button" 
                circle 
                type="primary" 
                @click="openImageModal"
                size="small"
              >
                <NIcon>
                  <Camera />
                </NIcon>
              </NButton>
            </div>

            <div class="profile-info">
              <h2>{{ normalizedProfile?.full_name || 'Your Profile' }}</h2>
              
              <div class="profile-status">
                <NTag type="success" round>
                  <template #icon>
                    <NIcon>
                      <Shield />
                    </NIcon>
                  </template>
                  Data Decrypted & Secure
                </NTag>
              </div>
            </div>

            <div class="profile-actions">
              <NButton type="primary" @click="editProfile" round>
                <template #icon>
                  <NIcon>
                    <EditIcon />
                  </NIcon>
                </template>
                Update Profile
              </NButton>
            </div>
          </div>
        </NCard>

        <!-- Personal Info Section -->
        <NCard title="Personal Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <ProfileIcon />
            </NIcon>
          </template>
          
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Date of Birth</div>
              <div class="info-value">{{ formatDate(normalizedProfile.dateOfBirth) }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Gender</div>
              <div class="info-value capitalize">{{ normalizedProfile.gender || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Address</div>
              <div class="info-value">{{ normalizedProfile.fullAddress || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Phone</div>
              <div class="info-value">
                <span class="phone-number">{{ formatPhoneNumber(normalizedProfile.phoneNumber) || 'Not provided' }}</span>
              </div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Email</div>
              <div class="info-value">{{ normalizedProfile.email || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Preferred Language</div>
              <div class="info-value">{{ normalizedProfile.preferredLanguage || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Preferred Communication</div>
              <div class="info-value">{{ normalizedProfile.preferred_communication_method || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Client Type</div>
              <div class="info-value capitalize">{{ normalizedProfile.client_type || 'Not provided' }}</div>
            </div>
          </div>
        </NCard>

        <!-- Emergency Contact Section -->
        <NCard title="Emergency Contact" class="profile-section">
          <template #header-extra>
            <NIcon>
              <Phone />
            </NIcon>
          </template>
          
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Contact Name</div>
              <div class="info-value">{{ normalizedProfile.emergency_contact_name || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Contact Phone</div>
              <div class="info-value">
                <span class="phone-number">{{ formatPhoneNumber(normalizedProfile.emergency_contact_phone) || 'Not provided' }}</span>
              </div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Relationship</div>
              <div class="info-value">{{ normalizedProfile.emergency_contact_relationship || 'Not provided' }}</div>
            </div>
          </div>
        </NCard>

        <!-- Legal Information Section -->
        <NCard title="Legal Information" class="profile-section">
          <template #header-extra>
            <NIcon>
              <Gavel />
            </NIcon>
          </template>
          
          <div class="legal-stats">
            <div class="stat-item">
              <div class="stat-icon">
                <NIcon>
                  <Clock />
                </NIcon>
              </div>
              <div class="stat-content">
                <div class="stat-label">Urgency Level</div>
                <div class="stat-value capitalize">{{ normalizedProfile.urgency_level || 'Not specified' }}</div>
              </div>
            </div>
            
            <div class="stat-item">
              <div class="stat-icon">
                <NIcon>
                  <DollarSign />
                </NIcon>
              </div>
              <div class="stat-content">
                <div class="stat-label">Legal Budget</div>
                <div class="stat-value">{{ formatCurrency(normalizedProfile.legal_budget) }}</div>
              </div>
            </div>
            
            <div class="stat-item">
              <div class="stat-icon">
                <NIcon>
                  <Star />
                </NIcon>
              </div>
              <div class="stat-content">
                <div class="stat-label">Referral Source</div>
                <div class="stat-value capitalize">{{ normalizedProfile.referral_source || 'Not provided' }}</div>
              </div>
            </div>
          </div>

          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <Briefcase />
            </NIcon>
            Legal Matters
          </h3>
          
          <div v-if="normalizedProfile.legal_matters && normalizedProfile.legal_matters.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(item, index) in normalizedProfile.legal_matters" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <CheckSquare />
                  </NIcon>
                </template>
                {{ item }}
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No legal matters provided</p>

          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <History />
            </NIcon>
            Previous Legal Issues
          </h3>
          
          <div v-if="normalizedProfile.previous_legal_issues && normalizedProfile.previous_legal_issues.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(item, index) in normalizedProfile.previous_legal_issues" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <AlertTriangle />
                  </NIcon>
                </template>
                {{ item }}
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No previous legal issues provided</p>

          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <Activity />
            </NIcon>
            Current Legal Cases
          </h3>
          
          <div v-if="normalizedProfile.current_legal_cases && normalizedProfile.current_legal_cases.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(item, index) in parseJsonData(normalizedProfile.current_legal_cases)" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <Gavel />
                  </NIcon>
                </template>
                <div class="case-details">
                  <strong>{{ item.caseType || item }}</strong>
                  <div v-if="item.status" class="case-status">Status: {{ item.status }}</div>
                  <div v-if="item.attorney" class="case-attorney">Attorney: {{ item.attorney }}</div>
                </div>
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No current legal cases provided</p>

          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <UserCheck />
            </NIcon>
            Previous Attorneys
          </h3>
          
          <div v-if="normalizedProfile.previous_attorneys && normalizedProfile.previous_attorneys.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(item, index) in parseJsonData(normalizedProfile.previous_attorneys)" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <Users />
                  </NIcon>
                </template>
                <div class="attorney-details">
                  <strong>{{ item.name || item }}</strong>
                  <div v-if="item.firm" class="attorney-firm">Firm: {{ item.firm }}</div>
                  <div v-if="item.practiceArea" class="attorney-practice">Practice Area: {{ item.practiceArea }}</div>
                </div>
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No previous attorneys provided</p>

          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <FileText />
            </NIcon>
            Legal Documents
          </h3>
          
          <div v-if="normalizedProfile.legal_documents && normalizedProfile.legal_documents.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(item, index) in parseJsonData(normalizedProfile.legal_documents)" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <FileCheck />
                  </NIcon>
                </template>
                <div class="document-details">
                  <strong>{{ item.type || item }}</strong>
                  <div v-if="item.description" class="document-description">{{ item.description }}</div>
                  <div v-if="item.date" class="document-date">Date: {{ formatDate(item.date) }}</div>
                </div>
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No legal documents provided</p>

          <NDivider />

          <h3 class="section-title">
            <NIcon class="section-icon">
              <Building />
            </NIcon>
            Court Cases
          </h3>
          
          <div v-if="normalizedProfile.court_cases && normalizedProfile.court_cases.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(item, index) in parseJsonData(normalizedProfile.court_cases)" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <Award />
                  </NIcon>
                </template>
                <div class="court-case-details">
                  <strong>{{ item.caseNumber || item }}</strong>
                  <div v-if="item.court" class="court-name">Court: {{ item.court }}</div>
                  <div v-if="item.status" class="court-status">Status: {{ item.status }}</div>
                  <div v-if="item.date" class="court-date">Date: {{ formatDate(item.date) }}</div>
                </div>
              </NListItem>
            </NList>
          </div>
          <p v-else class="empty-list-message">No court cases provided</p>
        </NCard>

        <!-- Business Information Section -->
        <NCard title="Business Information" class="profile-section" v-if="normalizedProfile.business_type">
          <template #header-extra>
            <NIcon>
              <Building2 />
            </NIcon>
          </template>
          
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Business Type</div>
              <div class="info-value capitalize">{{ normalizedProfile.business_type || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Industry</div>
              <div class="info-value capitalize">{{ normalizedProfile.business_industry || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Business Size</div>
              <div class="info-value">{{ normalizedProfile.business_size || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Annual Revenue</div>
              <div class="info-value">{{ formatCurrency(normalizedProfile.business_revenue) }}</div>
            </div>
          </div>
        </NCard>

        <!-- Personal Details Section -->
        <NCard title="Personal Details" class="profile-section">
          <template #header-extra>
            <NIcon>
              <User />
            </NIcon>
          </template>
          
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Employment Status</div>
              <div class="info-value capitalize">{{ normalizedProfile.employment_status || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Income Level</div>
              <div class="info-value">{{ normalizedProfile.income_level || 'Not provided' }}</div>
            </div>
          </div>

          <NDivider v-if="formatPersonalInfo(normalizedProfile.personal_info)" />

          <h3 v-if="formatPersonalInfo(normalizedProfile.personal_info)" class="section-title">
            <NIcon class="section-icon">
              <BookOpen />
            </NIcon>
            Additional Personal Information
          </h3>
          
          <div v-if="formatPersonalInfo(normalizedProfile.personal_info)">
            <div class="personal-info">
              <div 
                v-for="(item, index) in formatPersonalInfo(normalizedProfile.personal_info)" 
                :key="index"
                class="personal-item"
              >
                <div class="personal-label">{{ item.key }}</div>
                <div class="personal-value">{{ item.value }}</div>
              </div>
            </div>
          </div>
        </NCard>

        <!-- Financial Information Section -->
        <NCard title="Financial Information" class="profile-section" v-if="normalizedProfile.financial_info">
          <template #header-extra>
            <NIcon>
              <TrendingUp />
            </NIcon>
          </template>
          
          <div v-if="formatFinancialInfo(normalizedProfile.financial_info)">
            <div class="financial-info">
              <div 
                v-for="(item, index) in formatFinancialInfo(normalizedProfile.financial_info)" 
                :key="index"
                class="financial-item"
              >
                <div class="financial-label">{{ item.key }}</div>
                <div class="financial-value">{{ item.value }}</div>
              </div>
            </div>
          </div>

          <NDivider v-if="normalizedProfile.financial_info?.assets?.length > 0" />

          <h3 v-if="normalizedProfile.financial_info?.assets?.length > 0" class="section-title">
            <NIcon class="section-icon">
              <Banknote />
            </NIcon>
            Assets
          </h3>
          
          <div v-if="normalizedProfile.financial_info?.assets?.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(asset, index) in normalizedProfile.financial_info.assets" :key="index">
                <template #prefix>
                  <NIcon color="#1890ff">
                    <TrendingUp />
                  </NIcon>
                </template>
                <div class="asset-details">
                  <strong>{{ asset.type || 'Asset' }}</strong>
                  <div v-if="asset.value" class="asset-value">Value: {{ formatCurrency(asset.value) }}</div>
                  <div v-if="asset.description" class="asset-description">{{ asset.description }}</div>
                </div>
              </NListItem>
            </NList>
          </div>

          <NDivider v-if="normalizedProfile.financial_info?.liabilities?.length > 0" />

          <h3 v-if="normalizedProfile.financial_info?.liabilities?.length > 0" class="section-title">
            <NIcon class="section-icon">
              <CreditCard />
            </NIcon>
            Liabilities
          </h3>
          
          <div v-if="normalizedProfile.financial_info?.liabilities?.length > 0">
            <NList bordered class="styled-list">
              <NListItem v-for="(liability, index) in normalizedProfile.financial_info.liabilities" :key="index">
                <template #prefix>
                  <NIcon color="#ff4d4f">
                    <CreditCard />
                  </NIcon>
                </template>
                <div class="liability-details">
                  <strong>{{ liability.type || 'Liability' }}</strong>
                  <div v-if="liability.amount" class="liability-amount">Amount: {{ formatCurrency(liability.amount) }}</div>
                  <div v-if="liability.description" class="liability-description">{{ liability.description }}</div>
                </div>
              </NListItem>
            </NList>
          </div>
        </NCard>

        <!-- Special Requirements & Accessibility -->
        <NCard title="Special Requirements & Accessibility" class="profile-section">
          <template #header-extra>
            <NIcon>
              <AccessibilityIcon />
            </NIcon>
          </template>
          
          <div class="additional-info-grid">
            <div class="info-item">
              <div class="info-label">Accessibility Needs</div>
              <div v-if="normalizedProfile.accessibility_needs && normalizedProfile.accessibility_needs.length > 0">
                <NTag 
                  v-for="(need, index) in parseJsonData(normalizedProfile.accessibility_needs)" 
                  :key="index" 
                  type="info" 
                  round
                  size="medium"
                  class="accessibility-tag"
                >
                  {{ need.need || need }}
                </NTag>
              </div>
              <div v-else class="info-value">Not provided</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Special Requirements</div>
              <div class="info-value">{{ normalizedProfile.special_requirements || 'Not provided' }}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Conflict of Interest</div>
              <div class="info-value">
                <NTag :type="normalizedProfile.conflict_of_interest ? 'warning' : 'success'" round>
                  {{ normalizedProfile.conflict_of_interest ? 'Yes' : 'No' }}
                </NTag>
              </div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Background Check Authorization</div>
              <div class="info-value">
                <NTag :type="normalizedProfile.background_check ? 'success' : 'default'" round>
                  {{ normalizedProfile.background_check ? 'Authorized' : 'Not Authorized' }}
                </NTag>
              </div>
            </div>
          </div>

          <NDivider v-if="normalizedProfile.notes" />

          <div v-if="normalizedProfile.notes">
            <h3 class="section-title">
              <NIcon class="section-icon">
                <FileText />
              </NIcon>
              Additional Notes
            </h3>
            <div class="notes-box">{{ normalizedProfile.notes }}</div>
          </div>
        </NCard>
      </template>

      <!-- Show empty state if no profile exists -->
      <NCard v-if="!encryptedClientData && !clientProfile" class="profile-details">
        <NEmpty description="No client profile yet">
          <template #extra>
            <p>You haven't created your client profile yet. Create your profile to better manage your legal information.</p>
            <NButton type="primary" @click="editProfile">Create Profile</NButton>
          </template>
        </NEmpty>
      </NCard>
    </div>
  </div>

  <!-- Password Decryption Modal -->
  <NModal 
    v-model:show="showPasswordModal" 
    preset="card" 
    title="Decrypt Client Profile" 
    :closable="false"
    :mask-closable="false"
    :style="{ width: '400px' }"
  >
    <div class="password-modal-content">
      <div class="password-icon">
        <NIcon size="48">
          <Lock />
        </NIcon>
      </div>
      
      <p>Enter your client password to decrypt and view your legal data.</p>
      
      <NForm>
        <NFormItem>
          <NInput
            v-model:value="decryptionPassword"
            type="password"
            placeholder="Client Password"
            size="large"
            :disabled="isDecrypting"
            @keyup.enter="decryptData"
          />
        </NFormItem>
      </NForm>
      
      <div v-if="passwordError" class="password-error">
        <NAlert type="error" :title="passwordError" />
      </div>
    </div>
    
    <template #footer>
      <div class="modal-footer">
        <NSpace>
          <NButton @click="cancelDecryption" :disabled="isDecrypting">
            Cancel
          </NButton>
          <NButton 
            type="primary" 
            @click="decryptData" 
            :loading="isDecrypting"
            :disabled="!decryptionPassword.trim()"
          >
            <template #icon>
              <NIcon>
                <Key />
              </NIcon>
            </template>
            Decrypt Profile
          </NButton>
        </NSpace>
      </div>
    </template>
  </NModal>

  <!-- Profile Image Upload Modal -->
  <NModal v-model:show="showImageModal" preset="card" title="Upload Profile Picture" :style="{ width: '400px' }">
    <template #header-extra>
      <NButton circle size="small" @click="closeImageModal">
        <NIcon><CloseIcon /></NIcon>
      </NButton>
    </template>
    
    <div class="upload-modal-content">
      <div v-if="imageUploadError" class="upload-error">
        <NAlert type="error" :title="imageUploadError" closable />
      </div>
      
      <NUpload
        :custom-request="handleImageUpload"
        :max="1"
        accept="image/*"
        :show-file-list="false"
      >
        <NUploadDragger>
          <div class="upload-dragger-content">
            <NIcon size="48" class="upload-icon">
              <UploadIcon />
            </NIcon>
            <p>Click or drag image to this area to upload</p>
            <p class="upload-hint">
              Support for JPEG, PNG, or GIF, max size 2MB
            </p>
          </div>
        </NUploadDragger>
      </NUpload>
      
      <div v-if="isUploadingImage" class="upload-loading">
        <NSpin size="medium" />
        <p>Uploading your image...</p>
      </div>
    </div>
    
    <template #footer>
      <div class="modal-footer">
        <NButton @click="closeImageModal">Cancel</NButton>
      </div>
    </template>
  </NModal>
</template>

<style scoped>
.client-dashboard-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

/* Encrypted state styling */
.encrypted-state {
  margin-bottom: 20px;
}

.encryption-info-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #eaeffa;
}

.encryption-info {
  text-align: center;
  padding: 40px 20px;
}

.lock-icon {
  color: #1890ff;
  margin-bottom: 20px;
}

.encryption-info h3 {
  margin: 0 0 16px 0;
  color: #0e639c;
  font-size: 1.4rem;
}

.encryption-info p {
  margin: 0 0 24px 0;
  color: #666;
  font-size: 1rem;
}

.profile-header {
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background-color: #f9fbff;
  border: 1px solid #eaeffa;
}

.profile-header-content {
  display: flex;
  align-items: center;
  padding: 16px;
}

.profile-avatar-container {
  position: relative;
  margin-right: 24px;
}

.profile-avatar {
  cursor: pointer;
  width: 80px;
  height: 80px;
  font-size: 32px;
  background: linear-gradient(135deg, #2979ff, #1890ff);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.2);
  transition: transform 0.2s ease;
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.camera-button {
  position: absolute;
  right: -5px;
  bottom: -5px;
  z-index: 1;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.avatar-popover {
  padding: 8px;
  text-align: center;
}

.avatar-popover p {
  margin: 0 0 8px 0;
  font-size: 0.9rem;
  color: #666;
}

.profile-info {
  flex: 1;
}

.profile-info h2 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 1.5rem;
  color: #0e639c;
  font-weight: 600;
}

.profile-status {
  margin-top: 8px;
}

.profile-actions {
  margin-left: 24px;
}

.profile-section {
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  border: 1px solid #eaeffa;
}

.profile-section :deep(.n-card-header) {
  background-color: #f5f8ff;
  font-size: 1.1rem;
  font-weight: 600;
  color: #0e639c;
  padding: 16px 24px;
}

.profile-section :deep(.n-card__content) {
  padding: 20px 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 8px;
}

.info-item {
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 10px;
  transition: all 0.2s ease;
  border-left: 4px solid #1890ff;
}

.info-item:hover {
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.info-label {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 8px;
  font-weight: 500;
}

.info-value {
  font-size: 1.1rem;
  color: #333;
  font-weight: 500;
}

.capitalize {
  text-transform: capitalize;
}

.phone-number {
  font-family: monospace;
  letter-spacing: 0.5px;
  background-color: rgba(24, 144, 255, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.legal-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.stat-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 10px;
  transition: all 0.2s ease;
  border-left: 4px solid #1890ff;
}

.stat-item:hover {
  background-color: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  margin-right: 16px;
  color: #1890ff;
  background-color: rgba(24, 144, 255, 0.1);
  padding: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 6px;
  font-weight: 500;
}

.stat-value {
  font-size: 1.1rem;
  color: #333;
  font-weight: 600;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  color: #0e639c;
  margin-top: 24px;
  margin-bottom: 16px;
}

.section-icon {
  margin-right: 8px;
  color: #1890ff;
}

.styled-list :deep(.n-list-item) {
  padding: 12px 16px;
  transition: background-color 0.2s ease;
}

.styled-list :deep(.n-list-item:hover) {
  background-color: #f0f7ff;
}

.accessibility-tag {
  margin: 4px;
  padding: 6px 12px;
  font-weight: 500;
  transition: transform 0.2s ease;
}

.accessibility-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 6px rgba(24, 144, 255, 0.2);
}

.empty-list-message {
  color: #8c9bab;
  font-style: italic;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  text-align: center;
  font-size: 0.95rem;
}

.notes-box {
  background-color: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #1890ff;
  white-space: pre-line;
  font-style: italic;
}

.personal-info, .financial-info {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
}

.personal-item, .financial-item {
  background-color: #f9f9f9;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #1890ff;
}

.personal-label, .financial-label {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 4px;
  font-weight: 500;
}

.personal-value, .financial-value {
  font-size: 1rem;
  color: #333;
}

.case-details, .attorney-details, .document-details, .court-case-details, .asset-details, .liability-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.case-status, .case-attorney, .attorney-firm, .attorney-practice, .document-description, .document-date, .court-name, .court-status, .court-date, .asset-value, .asset-description, .liability-amount, .liability-description {
  font-size: 0.9rem;
  color: #666;
}

.additional-info-grid {
  margin-top: 16px;
}

/* Password modal styling */
.password-modal-content {
  padding: 20px 0;
  text-align: center;
}

.password-icon {
  color: #1890ff;
  margin-bottom: 20px;
}

.password-modal-content p {
  margin-bottom: 20px;
  color: #666;
}

.password-error {
  margin-top: 16px;
}

/* Upload modal styling */
.upload-modal-content {
  padding: 16px 0;
}

.upload-dragger-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.upload-icon {
  color: #1890ff;
  margin-bottom: 16px;
}

.upload-hint {
  font-size: 0.85rem;
  color: #999;
  margin-top: 8px;
}

.upload-error {
  margin-bottom: 16px;
}

.upload-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .client-dashboard-container {
    padding: 10px;
  }
  
  .profile-header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .profile-avatar-container {
    margin-right: 0;
    margin-bottom: 15px;
  }
  
  .profile-actions {
    margin-left: 0;
    margin-top: 15px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .legal-stats {
    flex-direction: column;
  }
  
  .personal-info, .financial-info {
    grid-template-columns: 1fr;
  }
}
</style>